import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import type { ApCreditDebitNoteListRow } from '@dcc-erp/shared';
import { fmt, fmtDate, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { useApBill, useRealizeApBillVat } from '../../lib/queries/ap';
import { downloadCertPdf } from '../../lib/queries/wht';
import { modalTitle } from '../ar/modal-style';
import { Drawer } from '../layout';

import type { CreditDebitNoteBill } from './CreateCreditDebitNoteModal';

// =============================================================================
// Drawer: AP bill detail (read-only this round). Opened from the voucherNo
// cell. Reuses useApBill (lines + VAT split + approvals) + the shared <Drawer>.
// B-phase adds the payments/50-ทวิ + ApprovalTimeline sections.
// =============================================================================
export interface ApBillDetailDrawerProps {
  companyId: string;
  billId: string;
  onClose: () => void;
  // AP Phase C2 — open the create-note modal seeded from this bill, and open a
  // note's engine approval panel from the drawer's note list.
  onCreateNote: (bill: CreditDebitNoteBill) => void;
  onOpenNote: (note: ApCreditDebitNoteListRow) => void;
}

export function ApBillDetailDrawer({
  companyId,
  billId,
  onClose,
  onCreateNote,
  onOpenNote,
}: ApBillDetailDrawerProps) {
  const { data: bill, isPending, error } = useApBill(companyId, billId);
  const realizeVat = useRealizeApBillVat(companyId);
  const [realizeOpen, setRealizeOpen] = useState(false);
  const [invNo, setInvNo] = useState('');
  const [invDate, setInvDate] = useState('');
  const [recvDate, setRecvDate] = useState('');

  const submitRealize = (): void => {
    if (!bill || !invNo || !invDate || !recvDate) return;
    realizeVat.mutate(
      {
        id: bill.id,
        data: { taxInvoiceNo: invNo, taxInvoiceDate: invDate, originalReceivedAt: recvDate },
      },
      {
        onSuccess: () => {
          setRealizeOpen(false);
          setInvNo('');
          setInvDate('');
          setRecvDate('');
        },
      },
    );
  };

  return (
    <Drawer onClose={onClose}>
      <div
        style={{
          padding: '18px 22px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h3 style={modalTitle}>{bill?.voucherNo ?? 'กำลังโหลด…'}</h3>
          {bill && (
            <div className="meta" style={{ marginTop: 2 }}>
              {bill.vendorName} · {fmtDate(bill.billDate)}
            </div>
          )}
        </div>
        <button type="button" className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
          <Icon name="x" size={18} />
        </button>
      </div>

      <div
        style={{
          padding: 22,
          overflow: 'auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        {error && (
          <div className="muted" style={{ color: 'var(--negative)' }}>
            โหลดไม่สำเร็จ: {error.message}
          </div>
        )}
        {isPending && <div className="muted">กำลังโหลด…</div>}
        {bill && (
          <>
            <div
              style={{
                padding: '14px 16px',
                background: '#fafafa',
                borderRadius: 8,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 14,
                fontSize: 14,
              }}
            >
              <div>
                <div className="meta">ผู้ขาย</div>
                {/* AP Phase C5 — vendor name links to the Vendor 360 / statement. */}
                <div style={{ fontWeight: 500 }}>
                  <Link
                    to="/ap/vendor/$vendorId"
                    params={{ vendorId: bill.vendorId }}
                    data-testid="drawer-vendor-link"
                    style={{ color: 'var(--brand)' }}
                  >
                    {bill.vendorName}
                  </Link>
                </div>
              </div>
              <div>
                <div className="meta">กำหนดจ่าย</div>
                <div style={{ fontWeight: 500 }}>{fmtDate(bill.dueDate)}</div>
              </div>
              <div>
                <div className="meta">สาขา / หน่วยงาน</div>
                <div style={{ fontWeight: 500 }}>{bill.branchName ?? '—'}</div>
              </div>
              <div>
                <div className="meta">สถานะ</div>
                <div style={{ fontWeight: 500 }}>{bill.status}</div>
              </div>
              {/* AP Phase C1 — early-payment discount window (from findBill). */}
              {bill.inDiscountWindow && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <span
                    className="badge positive"
                    data-testid="drawer-discount-badge"
                    style={{ fontSize: 12 }}
                  >
                    ส่วนลด {toNumber(bill.discountPercent)}%
                    {bill.discountDueDate ? ` ถึง ${fmtDate(bill.discountDueDate)}` : ''}
                    {toNumber(bill.discountAmount) > 0
                      ? ` (= ${fmt(toNumber(bill.discountAmount), 2)} บาท)`
                      : ''}
                  </span>
                </div>
              )}
            </div>

            <div>
              <div className="section-title" style={{ margin: '0 0 10px' }}>
                รายการค่าใช้จ่าย
              </div>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>คำอธิบาย</th>
                    <th>บัญชี</th>
                    <th className="num">ยอดเงิน</th>
                  </tr>
                </thead>
                <tbody>
                  {bill.lines.map((l) => (
                    <tr key={l.id}>
                      <td>{l.description}</td>
                      <td className="code">{l.expenseCode}</td>
                      <td className="num tnum">{fmt(toNumber(l.amount), 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                rowGap: 4,
                fontSize: 14,
              }}
            >
              <div className="muted">ยอดก่อน VAT</div>
              <div className="tnum" style={{ textAlign: 'right' }}>
                {fmt(toNumber(bill.subtotal), 2)}
              </div>
              <div className="muted">VAT</div>
              <div className="tnum" style={{ textAlign: 'right' }}>
                {fmt(toNumber(bill.vatAmount), 2)}
              </div>
              <div style={{ fontWeight: 600 }}>รวมทั้งสิ้น</div>
              <div className="tnum" style={{ textAlign: 'right', fontWeight: 600 }}>
                {fmt(toNumber(bill.totalAmount), 2)}
              </div>
              {/* AP Phase C2 — net note adjustment (signed) + the note-adjusted
                  คงค้าง. Only shown when there's a non-zero adjustment so plain
                  bills keep the original two-line summary. */}
              {toNumber(bill.noteAdjustment) !== 0 && (
                <>
                  <div className="muted">ปรับด้วยใบลด/เพิ่มหนี้</div>
                  <div className="tnum" style={{ textAlign: 'right' }}>
                    {fmt(toNumber(bill.noteAdjustment), 2)}
                  </div>
                </>
              )}
              <div style={{ fontWeight: 600 }}>คงค้าง</div>
              <div className="tnum" style={{ textAlign: 'right', fontWeight: 600 }}>
                {fmt(
                  bill.outstanding != null
                    ? toNumber(bill.outstanding)
                    : toNumber(bill.totalAmount) +
                        toNumber(bill.noteAdjustment) -
                        toNumber(bill.paidAmount),
                  2,
                )}
              </div>
              <div className="muted">JV ที่ลงบัญชี</div>
              <div className="tnum" style={{ textAlign: 'right' }}>
                {bill.journalEntryId ? 'ลงแล้ว' : '— รออนุมัติ —'}
              </div>
            </div>

            {/* AP Phase C2 — credit/debit notes against this bill + a create
                action for POSTED bills. The note list comes from findBill
                (creditDebitNotes); clicking a note opens its engine panel. */}
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  margin: '0 0 10px',
                }}
              >
                <div className="section-title" style={{ margin: 0 }}>
                  ใบลดหนี้ / ใบเพิ่มหนี้
                </div>
                {bill.status === 'POSTED' && (
                  <button
                    className="btn sm"
                    data-testid="drawer-create-note"
                    onClick={() =>
                      onCreateNote({
                        id: bill.id,
                        voucherNo: bill.voucherNo,
                        vatRate: bill.vatRate,
                        outstanding:
                          bill.outstanding != null
                            ? toNumber(bill.outstanding)
                            : toNumber(bill.totalAmount) +
                              toNumber(bill.noteAdjustment) -
                              toNumber(bill.paidAmount),
                        ...(bill.lines[0]?.expenseCode
                          ? { defaultExpenseCode: bill.lines[0].expenseCode }
                          : {}),
                      })
                    }
                  >
                    <Icon name="plus" size={12} /> ใบลดหนี้/เพิ่มหนี้
                  </button>
                )}
              </div>
              {(bill.creditDebitNotes ?? []).length === 0 ? (
                <div className="muted" style={{ fontSize: 13 }}>
                  ยังไม่มีใบลดหนี้ / ใบเพิ่มหนี้
                </div>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>เลขที่</th>
                      <th>ประเภท</th>
                      <th>วันที่</th>
                      <th className="num">ยอดรวม</th>
                      <th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bill.creditDebitNotes ?? []).map((n) => (
                      <tr
                        key={n.id}
                        style={{ cursor: 'pointer' }}
                        data-testid={`drawer-note-row-${n.id}`}
                        onClick={() =>
                          onOpenNote({
                            // The panel re-fetches full detail via useApCreditDebitNote;
                            // this lean row only seeds its header while loading. The cast
                            // narrows the summary to the list-row shape the panel accepts.
                            ...n,
                            companyId,
                            billId: bill.id,
                            noteDate: n.noteDate,
                            currentStep: 0,
                          } as unknown as ApCreditDebitNoteListRow)
                        }
                      >
                        <td className="code">{n.voucherNo}</td>
                        <td>
                          {n.noteType === 'CREDIT' ? (
                            <span className="badge positive">
                              <span className="bdot" />
                              ลดหนี้
                            </span>
                          ) : (
                            <span className="badge warning">
                              <span className="bdot" />
                              เพิ่มหนี้
                            </span>
                          )}
                        </td>
                        <td>{fmtDate(n.noteDate)}</td>
                        <td className="num tnum">{fmt(toNumber(n.totalAmount), 2)}</td>
                        <td>
                          {n.status === 'POSTED' ? (
                            <span className="badge positive">
                              <span className="bdot" />
                              ลงบัญชีแล้ว
                            </span>
                          ) : n.status === 'VOID' ? (
                            <span className="badge negative">
                              <span className="bdot" />
                              ยกเลิก
                            </span>
                          ) : n.approvalStatus === 'PENDING' ? (
                            <span className="badge warning">
                              <span className="bdot" />
                              รออนุมัติ
                            </span>
                          ) : (
                            <span className="badge info">
                              <span className="bdot" />
                              ร่าง
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* BT-7: payments + 50-ทวิ — lists each payment of this bill with
                its withholding certificates and an inline พิมพ์ 50 ทวิ button.
                e-WHT certs are issued by the bank → no in-house printout
                (mirrors the /wht cert list). */}
            <div>
              <div className="section-title" style={{ margin: '0 0 10px' }}>
                การจ่าย / ใบ 50 ทวิ
              </div>
              {(bill.payments50tawi ?? []).length === 0 ? (
                <div className="muted" style={{ fontSize: 13 }}>
                  ยังไม่มีการจ่าย
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {(bill.payments50tawi ?? []).map((p) => (
                    <div
                      key={p.paymentId}
                      style={{
                        border: '1px solid var(--border)',
                        borderRadius: 8,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          background: '#fafafa',
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        <span className="code">{p.paymentVoucherNo}</span>
                        <span className="meta">{fmtDate(p.paymentDate)}</span>
                      </div>
                      <table className="tbl">
                        <thead>
                          <tr>
                            <th>เลขที่ 50 ทวิ</th>
                            <th>ประเภทเงินได้</th>
                            <th className="num">ภาษีหัก</th>
                            <th style={{ minWidth: 160 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {p.certs.map((c) => (
                            <tr key={c.id}>
                              <td className="code">{c.voucherNo}</td>
                              <td>{c.incomeType}</td>
                              <td className="num tnum">{fmt(toNumber(c.amount), 2)}</td>
                              <td>
                                {c.channel === 'E_WHT' ? (
                                  <span className="meta" style={{ fontSize: 13 }}>
                                    ออกโดยธนาคาร (e-WHT)
                                  </span>
                                ) : (
                                  <button
                                    className="btn sm ghost"
                                    onClick={() => void downloadCertPdf(companyId, c.id)}
                                  >
                                    <Icon name="print" size={12} /> พิมพ์ 50 ทวิ
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* B3: realize-VAT action — shown only for SUSPENSE bills */}
            {bill.vatCreditStatus === 'SUSPENSE' && (
              <div
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  padding: 14,
                  background: '#fffbeb',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                  ภาษีซื้อยังไม่ถึงกำหนด (10190013)
                </div>
                {!realizeOpen ? (
                  <button className="btn sm" onClick={() => setRealizeOpen(true)}>
                    <Icon name="check" size={12} /> รับใบกำกับภาษีต้นฉบับแล้ว
                  </button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={{ fontSize: 13 }}>
                      เลขที่ใบกำกับภาษี
                      <input
                        className="input"
                        value={invNo}
                        onChange={(e) => setInvNo(e.target.value)}
                      />
                    </label>
                    <label style={{ fontSize: 13 }}>
                      วันที่ใบกำกับ
                      <input
                        className="input"
                        type="date"
                        value={invDate}
                        onChange={(e) => setInvDate(e.target.value)}
                      />
                    </label>
                    <label style={{ fontSize: 13 }}>
                      วันที่รับเอกสาร
                      <input
                        className="input"
                        type="date"
                        value={recvDate}
                        onChange={(e) => setRecvDate(e.target.value)}
                      />
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn primary sm"
                        onClick={submitRealize}
                        disabled={realizeVat.isPending}
                      >
                        ยืนยันรับใบกำกับ
                      </button>
                      <button className="btn sm" onClick={() => setRealizeOpen(false)}>
                        ยกเลิก
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}
